/**
 * Onboarding, dashboard hero/checklist and magic-link events.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const ONBOARDING_EVENTS = {
  // Onboarding
  ONBOARDING_STARTED: 'Onboarding Started',
  /**
   * Workspace activation failed, so the user is stuck on the retry screen and
   * `Onboarding Started` will never fire for them. Without this an activation
   * outage is INVISIBLE — and worse than invisible, it flatters the numbers:
   * starts fall while the completion RATE rises on a smaller denominator.
   */
  ONBOARDING_ACTIVATION_FAILED: 'Onboarding Activation Failed',
  /**
   * The wizard opened somewhere other than step one.
   * `{ step_name, source: 'draft' | 'magic_link' }`.
   *
   * `source` is the load-bearing half: `draft` is a returning user resuming on
   * the same browser, `magic_link` is the desktop→phone hop. They are different
   * journeys with different drop-off, and without the dimension a rise in
   * resumes can't be told from a rise in phone handoffs.
   */
  ONBOARDING_RESUMED: 'Onboarding Resumed',
  // PROPERTY BOUNDARY — 2026-08-01 (5-step redesign). The event NAME is
  // unchanged but its `step_number` / `step_name` vocabulary was fully
  // redefined, and the flow ships to 100% of signups on one deploy with no
  // flag. Insights filtering the old values return zero from that date, and a
  // funnel keyed on `step_number` silently changes meaning:
  //   1  profile_name                     -> you
  //   2  research_profile                 -> research
  //   3  orcid_id                         -> knowledge | join
  //   4  create_workspace | join_workspace-> ask
  //   5  import                           -> mobile
  // `Onboarding Research Profile Completed` also dropped field_skipped /
  // role_skipped / lab_size_skipped / fully_skipped. Annotate the deploy in
  // PostHog so the series discontinuity is visible on the charts.
  ONBOARDING_STEP_COMPLETED: 'Onboarding Step Completed',
  ONBOARDING_COMPLETED: 'Onboarding Completed',

  // Onboarding v2 (research profile + seeding + first-run tailoring)
  ONBOARDING_RESEARCH_PROFILE_COMPLETED: 'Onboarding Research Profile Completed',
  ONBOARDING_RESEARCH_PROFILE_OTHER_CAPTURED: 'Onboarding Research Profile Other Captured',

  // Onboarding redesign (5-step flow: You → Research → Knowledge → Ask → Mobile).
  // Step-scoped events named to the CURRENT step order — replaces the stale
  // `ONBOARDING_STEP3_*` names below, whose numbers drifted after renumbering.
  ONBOARDING_KNOWLEDGE_SHOWN: 'Onboarding Knowledge Shown',
  ONBOARDING_IMPORT_STARTED: 'Onboarding Import Started',
  ONBOARDING_IMPORT_SUBMITTED: 'Onboarding Import Submitted',
  ONBOARDING_IMPORT_COMPLETED: 'Onboarding Import Completed',
  ONBOARDING_KNOWLEDGE_SKIPPED: 'Onboarding Knowledge Skipped',
  ONBOARDING_ASK_SHOWN: 'Onboarding Ask Shown',
  ONBOARDING_ASK_PROMPT_SELECTED: 'Onboarding Ask Prompt Selected',
  ONBOARDING_ASK_ANSWERED: 'Onboarding Ask Answered',
  ONBOARDING_ASK_SKIPPED: 'Onboarding Ask Skipped',
  // PROPERTY BOUNDARY — 2026-08-07. Name unchanged; emitter, trigger and
  // properties all moved. It was fired by the two Ask handlers, so it meant
  // "left Ask"; it now fires from the Mobile step's own mount, so it means
  // "the Mobile step was shown", and gains `qr_shown` (false on a phone, where
  // the QR is deliberately not rendered).
  // Two consequences for anyone reading the funnel across the boundary:
  // per-user counts RISE, because a refresh on Mobile now resumes back to
  // Mobile and re-mounts where it previously dropped the user on Knowledge;
  // and the magic-link arrival now counts at all, which is the point of the
  // change. Unique-user funnels are unaffected. Annotate the deploy in PostHog.
  ONBOARDING_MOBILE_SHOWN: 'Onboarding Mobile Shown',
  // The always-on dashboard hero (chat input + personalised prompts) —
  // successor surface to the first-run hero.
  DASHBOARD_HERO_SHOWN: 'Dashboard Hero Shown',
  // "Pick up where you left off" resume-row click on the dashboard.
  DASHBOARD_RESUME_CLICKED: 'Dashboard Resume Clicked',
  SUGGESTED_PROMPT_SHOWN: 'Suggested Prompt Shown',
  // PROPERTY BOUNDARY — 2026-08-01. Name unchanged; contract replaced. The
  // stable CTR dimension `prompt_key` was dropped for `prompt_template_id`
  // (disjoint value set), and `source: 'first_run_hero'` has no emitter left.
  // The underlying object changed too — a deterministic 3-template matrix
  // became a server-derived personalised pool — so pre/post CTR is not
  // comparable even where categories look alike.
  SUGGESTED_PROMPT_CLICKED: 'Suggested Prompt Clicked',

  // Dashboard "Get set up" onboarding card grid (evergreen, dismissible).
  DASHBOARD_ONBOARDING_SHOWN: 'Dashboard Onboarding Shown',
  DASHBOARD_ONBOARDING_CARD_CLICKED: 'Dashboard Onboarding Card Clicked',
  DASHBOARD_ONBOARDING_DISMISSED: 'Dashboard Onboarding Dismissed',

  // Provider-aware connector onboarding hero (dashboard). Client-side
  // complements the server-side `User Signed Up` / `SSO Sign In Completed`.
  CONNECTOR_HERO_SHOWN: 'Connector Hero Shown',
  CONNECTOR_HERO_CONNECTED: 'Connector Hero Connected',

  // Onboarding checklist
  ONBOARDING_STEP3_SHOWN: 'Onboarding Step 3 Shown',
  ONBOARDING_STEP3_IMPORT_STARTED: 'Onboarding Step 3 Import Started',
  ONBOARDING_STEP3_IMPORT_COMPLETED: 'Onboarding Step 3 Import Completed',
  ONBOARDING_STEP3_SKIPPED: 'Onboarding Step 3 Skipped',
  ONBOARDING_CHECKLIST_SHOWN: 'Onboarding Checklist Shown',
  ONBOARDING_CHECKLIST_STEP_COMPLETED: 'Onboarding Checklist Step Completed',
  ONBOARDING_CHECKLIST_DISMISSED: 'Onboarding Checklist Dismissed',
  ONBOARDING_CHECKLIST_ALL_COMPLETE: 'Onboarding Checklist All Complete',
  // Unified setup list (checklist-2026-11). Props: { variant }.
  ONBOARDING_CHECKLIST_COLLAPSED: 'Onboarding Checklist Collapsed',
  ONBOARDING_CHECKLIST_SHOWN_AGAIN: 'Onboarding Checklist Shown Again',
  // The Start here tile was opened. Props: { source: 'seeded' | 'team_protocol'
  // | 'recents', variant }.
  ONBOARDING_START_HERE_OPENED: 'Onboarding Start Here Opened',
  ONBOARDING_LIVE_TEASER_CLICKED: 'Onboarding Live Teaser Clicked',
  ONBOARDING_INVITE_SKIPPED: 'Onboarding Invite Skipped',
  ONBOARDING_MOBILE_CAPTURE_STARTED: 'Onboarding Mobile Capture Started',
  MAGIC_LINK_GENERATED: 'Magic Link Generated',
  MAGIC_LINK_QR_DISPLAYED: 'Magic Link QR Displayed',
  MAGIC_LINK_REDEEMED: 'Magic Link Redeemed',
  MAGIC_LINK_REDEEM_FAILED: 'Magic Link Redeem Failed',
  /** A "get the Bower app" QR (bowerlabs.ai/app) was rendered. `src` is the
   *  placement; the scans themselves are `App QR Scanned`, sent by the website. */
  APP_INSTALL_QR_DISPLAYED: 'App Install QR Displayed',
  /** The user swapped an app-install QR for the browser sign-in QR. `src`. */
  APP_INSTALL_QR_BROWSER_CHOSEN: 'App Install QR Browser Chosen',
  /** The phone browser's "Get the app" card was shown on the capture page. */
  APP_INSTALL_BANNER_SHOWN: 'App Install Banner Shown',
  /** The user tapped "Get the app" on that card (it opens bowerlabs.ai/app). */
  APP_INSTALL_BANNER_CLICKED: 'App Install Banner Clicked',
  /** The user closed that card to keep using the browser. */
  APP_INSTALL_BANNER_DISMISSED: 'App Install Banner Dismissed',
} as const;
